import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useToast } from '../../context/ToastContext';
import { fetchEvents } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const typeFilters = [
  { value: 'all', label: 'All' },
  { value: 'special', label: 'Special requests only' },
];
const statusFilters = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function EventsPage() {
  const toast = useToast();
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    fetchEvents({
      isSpecialRequest: typeFilter === 'special' ? true : undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
    })
      .then((data) => { if (isMounted) setEvents(data); })
      .catch((e) => { if (isMounted) setError(e?.message || 'Failed to load events'); })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, [typeFilter, statusFilter]);

  const handleExport = () => {
    const rows = events.map((e) => ({
      id: e.id,
      title: e.title,
      event_type: e.event_type,
      event_date: e.event_date ? format(new Date(e.event_date), 'yyyy-MM-dd') : '',
      estimated_guests: e.estimated_guests ?? '',
      location: e.location ?? '',
      status: e.status,
      is_special_request: e.is_special_request ?? false,
      request_fee: e.request_fee ?? '',
      request_fee_paid: e.request_fee_paid ?? false,
      request_fee_refunded: e.request_fee_refunded ?? false,
      customer_email: e.customer?.email ?? '',
    }));
    if (!rows.length) {
      toast.info('No events to export');
      return;
    }
    exportToCsv(rows, [
      { key: 'id', label: 'ID' },
      { key: 'title', label: 'Title' },
      { key: 'event_type', label: 'Type' },
      { key: 'event_date', label: 'Date' },
      { key: 'estimated_guests', label: 'Guests' },
      { key: 'location', label: 'Location' },
      { key: 'status', label: 'Status' },
      { key: 'is_special_request', label: 'Special request' },
      { key: 'request_fee', label: 'Request fee' },
      { key: 'request_fee_paid', label: 'Fee paid' },
      { key: 'request_fee_refunded', label: 'Fee refunded' },
      { key: 'customer_email', label: 'Customer' },
    ], downloadFilename('events'));
    toast.success(`Exported ${rows.length} events`);
  };

  const specialCount = events.filter((e) => e.is_special_request).length;
  const feePaidCount = events.filter((e) => e.request_fee_paid).length;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Events &amp; special requests</h2>
          <p className="text-sm text-muted-foreground">
            View events, special requests, and request fees.
          </p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
        >
          <Icon name="Download" size={16} /> Export
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Total events</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{events.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Special requests</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{specialCount}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Request fee paid</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{feePaidCount}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {typeFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setTypeFilter(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              typeFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="mx-2 text-muted-foreground">|</span>
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              statusFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Event</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-left font-semibold">Guests</th>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-left font-semibold">Special</th>
                <th className="px-4 py-3 text-left font-semibold">Request fee</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">Loading…</td>
                </tr>
              )}
              {!loading && !events.length && (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">No events found.</td>
                </tr>
              )}
              {!loading && events.map((e) => (
                <tr key={e.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium text-foreground">{e.title || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.event_type || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {e.event_date ? format(new Date(e.event_date), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3">{e.estimated_guests ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.customer?.email ?? '—'}</td>
                  <td className="px-4 py-3">
                    {e.is_special_request ? (
                      <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">Yes</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {e.request_fee != null && Number(e.request_fee) > 0 ? (
                      <span>
                        R{Number(e.request_fee).toLocaleString()}
                        {e.request_fee_paid && <span className="ml-1 text-emerald-600">✓</span>}
                        {e.request_fee_refunded && <span className="ml-1 text-muted-foreground">(refunded)</span>}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      e.status === 'open' ? 'bg-emerald-100 text-emerald-700' :
                      e.status === 'closed' ? 'bg-sky-100 text-sky-700' :
                      e.status === 'cancelled' ? 'bg-rose-100 text-rose-700' : 'bg-muted text-foreground'
                    }`}>
                      {e.status || '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
